import { Spacer, Text } from "@hackthenorth/north";
import { PlusCircleIcon } from "@heroicons/react/24/outline";
import { ExclamationCircleIcon } from "@heroicons/react/24/solid";
import React, { useState } from "react";
import styled from "styled-components";

import { Modal } from "src/shared/components";
import { BalanceChip } from "src/views/goose-games/components/balanceChip";
import { useAdjustGooseGamePlayerPointsMutation } from "src/views/goose-games/graphql/adjustGooseGamePlayerPoints.generated";

import FooterActions from "../../../../footerActions";

export interface AddPointsModalProps {
  isOpen: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  currentBalance: number;
  eventAttendeeId: number;
}
const AddPointsModal: React.FC<AddPointsModalProps> = ({
  isOpen,
  onConfirm,
  onCancel,
  currentBalance,
  eventAttendeeId,
}) => {
  const [amount, setAmount] = useState<number | undefined>(undefined);
  const [reason, setReason] = useState("");
  const [adjustPoints] = useAdjustGooseGamePlayerPointsMutation();

  const handleConfirm = async () => {
    if (amount === undefined || amount === 0) {
      alert("Please enter a valid amount.");
      return;
    }
    try {
      await adjustPoints({
        variables: {
          event_attendee_id: eventAttendeeId,
          points_change: amount,
          note: reason,
        },
      });
      onConfirm();
    } catch (error) {
      console.error("Error adjusting points:", error);
    }
  };

  return (
    <Modal isOpen={isOpen}>
      <HeaderContainer>
        <HeaderIcon>
          <PlusCircleIcon />
        </HeaderIcon>
        <Text mods="h3 bold">Add points</Text>
      </HeaderContainer>
      <Wrapper>
        <AmountWrapper>
          <Text mods="body bold">Amount to be added:</Text>
          <Input
            type="number"
            placeholder="i.e. 50"
            width="96px"
            onBlur={(e) => setAmount(Number(e.target.value))}
          />
        </AmountWrapper>
        <Text mods="body bold">Reason</Text>
        <Input
          type="text"
          placeholder="Why are you adding points?"
          onBlur={(e) => setReason(e.target.value)}
        />
        <BalanceContainer>
          <BalanceWrapper currentBalance={true}>
            <Text mods="body bold">Current Balance:</Text>
            <BalanceChip points={currentBalance} />
          </BalanceWrapper>
          <BalanceWrapper>
            <Text mods="body bold">New Balance:</Text>
            <BalanceChip points={currentBalance + (amount || 0)} />
          </BalanceWrapper>
        </BalanceContainer>
        {amount && reason ? (
          <WarningWrapper>
            <WarningIcon>
              <ExclamationCircleIcon color="#D97706" width="18px" />
            </WarningIcon>
            <Text
              mods="body textWarning"
              style={{ whiteSpace: "pre-line", lineHeight: "1.5" }}
            >
              Are you sure you want to proceed? {"\n"} You can&apos;t undo this
              action.
            </Text>
          </WarningWrapper>
        ) : (
          <></>
        )}
      </Wrapper>

      <Spacer height={26} />
      <FooterActions
        confirmText={"Confirm"}
        onConfirm={handleConfirm}
        onCancel={onCancel}
        isConfirmDisabled={!amount || !reason}
      />
    </Modal>
  );
};

export default AddPointsModal;

const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  padding-bottom: 24px;
  gap: 8px;
`;

const HeaderIcon = styled.div<{ backgroundColor?: string }>`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ backgroundColor }) => backgroundColor};
  border-radius: 50%;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const AmountWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Input = styled.input<{ width?: string }>`
  width: ${({ width }) => width || "100%"};
  padding: 10px 16px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.globalConstants.color.borderGray};

  &:focus {
    outline: none;
    border-color: #0070f3;
  }
  &::placeholder {
    color: ${({ theme }) => theme.globalConstants.color.textTertiary};
  }
`;

const BalanceContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
`;

const BalanceWrapper = styled.div<{ currentBalance?: boolean }>`
  background-color: ${({ currentBalance, theme }) =>
    currentBalance
      ? theme.globalConstants.color.backgroundGray
      : theme.globalConstants.color.backgroundPrimaryLight};
  padding: 12px 16px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const WarningWrapper = styled.div`
  display: flex;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 8px;
  align-items: start;
  background-color: ${({ theme }) =>
    theme.globalConstants.color.backgroundWarningLight};
`;

const WarningIcon = styled.div`
  padding: 4px 0;
`;
